import React, { useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { copyToClipboard } from 'common/util'
import ReactTooltip from 'react-tooltip'

// Components
import BookmarkRow from './BookmarkRow'
import PlaceRow from './PlaceRow'
import NoBookmarksMessage from './NoBookmarksMessage'
import BigSpinner from 'components/Utilities/BigSpinner'
import withFlight from 'containers/withFlight'
import withStore from 'components/withStore'

// Redux
import {
  fetchBookmarks,
  removeBookmark,
  getBookmarkSources,
  getFollowedPlaces,
  hasFollowedPlaces,
  hasBookmarksAvailable,
  getBookmarksNeedInitialize,
  removeFollow,
  getCurrentPage,
  getLoadedPages,
  getTotalPages,
  getIsLoading,
} from 'ducks/bookmarks'

import {
  getActiveSourceIds,
  fetchSourceIfNeeded,
  // activate,
} from 'ducks/sources'
import { toggleBookmarkSource } from '../../ducks/mapProperties'

const BookmarksPane = ({ passEventToFlight }) => {
  const bookmarkSources = useSelector(getBookmarkSources)
  const bookmarksNeedInit = useSelector(getBookmarksNeedInitialize)
  const hostDomain = window.data.host
  const dispatch = useDispatch()
  const hasBookmarks = useSelector(hasBookmarksAvailable)
  const hasPlaces = useSelector(hasFollowedPlaces)
  const followedPlaces = useSelector(getFollowedPlaces)
  const activeSourceIds = useSelector(getActiveSourceIds) // may be array of numbers or strings
  const loadedPages = useSelector(getLoadedPages)
  const totalPages = useSelector(getTotalPages)
  const page = useSelector(getCurrentPage)
  const isLoading = useSelector(getIsLoading)
  const scrollRef = useRef(null)

  useEffect(() => {
    dispatch(fetchBookmarks(page))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookmarksNeedInit])

  function postNotificationMessage(message) {
    passEventToFlight('notify:message', { message })
  }

  const handleAddressClick = (e, bookmark) => {
    e.preventDefault()
    passEventToFlight('property:request', { path: bookmark.path, address: bookmark.address })
  }

  const handleCopyClick = (path) => {
    copyToClipboard(`https://${hostDomain}${path}`)
    postNotificationMessage('Parcel link copied to clipboard.')
  }

  const handleDeletePlace = (followId) => {
    const callback = {
      success: () => postNotificationMessage('Unfollowed.'),
      failed:  () => postNotificationMessage(`Could not unfollow.`)
    }
    dispatch(removeFollow(followId, callback))
  }

  const handleDeleteClick = (path, sourceId) => {
    const callback = {
      success: () => {
        passEventToFlight('tiles:reload')
        postNotificationMessage('Bookmark removed.')
      },
      failed: () => postNotificationMessage('Bookmark could not be removed.')
    }
    dispatch(removeBookmark(path, sourceId, callback))
  }

  /*const handleDeleteAllClick = (e, sourceId) => {
    e.preventDefault();
    const callback = {
      success: () => postNotificationMessage('Bookmarks have been removed.'),
      failed: () => postNotificationMessage('Bookmarks could not be removed.')
    }
    if(window.confirm('Are you sure you wish to permanently delete all bookmarks from this list?')) {
      dispatch(removeAllBookmarks(sourceId, callback));
    }
  };*/

  const handleExportClick = () => {
    passEventToFlight('pane:request:show', {tab: '#export'})
  }

  const addSourceToMap = (sourceId) => {
    // debug(`BookmarksPane adding ${sourceId}`)
    const callback = {
      success: (src) => {
        // debug('BookmarksPane src added: ', src)
        postNotificationMessage('Added to map.')
        passEventToFlight('sources:add', src)
        //dispatch(activate(sourceId));  // not implemented all the way
        dispatch(toggleBookmarkSource())
      },
      failed: () => postNotificationMessage(`Could not add dataset to the map.`)
    }
    dispatch(fetchSourceIfNeeded(sourceId, callback))
  }

  const removeSourceFromMap = (sourceId) => {
    // debug(`BookmarksPane removing ${sourceId}`)
    const callback = {
      success: (src) => {
        postNotificationMessage('Removed from map.')
        passEventToFlight('sources:remove', src)
        dispatch(toggleBookmarkSource())
      },
      failed: () => postNotificationMessage(`Could not remove dataset from the map.`)
    }
    dispatch(fetchSourceIfNeeded(sourceId, callback))
  }

  const loadNextPage = async (pageToLoad) => {
    await dispatch(fetchBookmarks(pageToLoad))
  }

  const handleScroll = () => {
    const el = scrollRef.current

    if(!el || isLoading) return

    const scrollBottom = el.scrollTop + el.clientHeight
    const pixelsFromBottom = 200

    if(scrollBottom >= el.scrollHeight - pixelsFromBottom) {
      const nextPage = page + 1

      if(nextPage > totalPages) {
        return
      }
      if(!loadedPages.includes(nextPage)) {
        loadNextPage(nextPage)
      }
    }
  }

  const intro = <p>
    <span className='subtle'>
      Places and properties you follow will appear here. You&apos;ll be notified when information about them changes.&nbsp;
      {window.data.signed_in && _.isEmpty(window.data.notifications) &&
        <a href='/notify/follows'>Set your email and mobile push notifications here!</a>}
    </span>
  </p>

  let body = null

  if(hasBookmarks || hasPlaces) {
    body = <>

      {hasPlaces && <div>
        <h4>Places</h4>
        <div className="bookmarks-container">
          {followedPlaces.map((follow) =>
            <PlaceRow
              follow={follow}
              key={follow.id}
              handleDeleteClick={handleDeletePlace}/>
          )}
        </div>
      </div>}

      {hasBookmarks && <div>
        {bookmarkSources.map((list) => (
          <React.Fragment key={list.source_id}>
            {!activeSourceIds.map(String).includes(String(list.source_id)) ? (
              <a href='#'
                className='btn btn-xs btn-round btn-info pull-right'
                onClick={() => { addSourceToMap(list.source_id) }}>
                Show on map
              </a>
            ) : (
              <a href='#'
                className='btn btn-xs btn-round btn-warning pull-right'
                onClick={() => { removeSourceFromMap(list.source_id) }}>
                Remove from map
              </a>
            )}
            <h4>{list.name}</h4>
            <div
              className="bookmarks-container bookmarks-scroll-container"
              ref={scrollRef}
              onScroll={handleScroll}
              // style={{ height: '200px' }}
            >
              {list.bookmarks.map((bookmark) =>
                <BookmarkRow
                  key={bookmark.id}
                  bookmark={bookmark}
                  handleAddressClick={handleAddressClick}
                  handleCopyClick={handleCopyClick}
                  handleDeleteClick={handleDeleteClick}
                />
              )}
            </div>
          </React.Fragment>
        ))}

        <div className="download-btn-container">
          <p className="subtle right margin-btm-none" data-tip data-for="download-csv-tooltip">
            {/* Downloading your follows */}
            {/* {' '} */}
            <i className="fas fa-info-circle"></i>
          </p>
          <a href="/profile/bookmarks.csv" target="_blank" className="btn btn-primary btn-block margin-top-xs">Download Follows as CSV</a>
          <ReactTooltip
            id="download-csv-tooltip"
            className="tt-width-300"
            place="right"
            effect="solid"
            clickable={true}
            delayHide={1000}
          >
            <span>
              Note: This button will give a plain list of your Followed properties.
              <br />
              If you&apos;d like to download Followed properties with Regrid&apos;s property data attached,
              <br />
              please use our <a href="#" onClick={handleExportClick} style={{ color: '#fff', textDecoration: 'underline' }}>
                Export Page
              </a>
            </span>
          </ReactTooltip>
        </div>
      </div>}

    </>

  } else if(bookmarksNeedInit) {
    body = <BigSpinner/>

  } else {
    // Doesn't have any bookmarks at the moment
    body = <NoBookmarksMessage />
  }

  return <>
    {intro}
    {body}
  </>
}

BookmarksPane.propTypes = {
  passEventToFlight:   PropTypes.func.isRequired
}

export default withStore(withFlight(BookmarksPane))
